import { useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { createVendorConnectionUrl } from '../../apis'
import { useAuthFetchWithResponse } from '@e3dc-react/shell/Hooks/AuthFetch'
import { AuthFetchError } from '@e3dc-react/shell/Types/api'
import { ExtensionConnectionTypes } from '../../types'
import { useTypedSelector } from 'src/State/RootReducer'
import { confirmVendorConnection, deleteVendorConnection } from '../../apis/create-vendor-connection-url'
import { useSnackbar } from '@e3dc-react/shell/Hooks/Snackbar'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { usePortalMutation } from 'src/Libs/api'

interface ReturnType {
  extensionConnectionUrl: string | null
  createExtensionConnectionUrl: () => void
  handleDeleteExtensionConnection: () => Promise<boolean | null>
  handleExtensionConnectionConfirmation: (installationId?: string, vendorAccountId?: string) => void
  connectionState: ConnectionState
  hasConnectionUrlError: boolean
}

interface ConnectionState {
  isConnectionConfirmed: boolean
  isLoading: boolean
  hasError: boolean
  error: Error | AuthFetchError | null
  errorNumber?: number
}

/**
 * This hook creates a extension connection from which it get's a url to redirect to
 */
const useExtensionConnection = (ExtensionName: ExtensionConnectionTypes): ReturnType => {
  const [extensionConnectionUrl, setExtensionConnectionUrl] = useState<string | null>(null)
  const [connectionState, setConnectionState] = useState<ConnectionState>({
    isConnectionConfirmed: false,
    isLoading: false,
    hasError: false,
    errorNumber: undefined,
    error: null,
  })

  const authFetch = useAuthFetchWithResponse()
  const installationId = useTypedSelector(state => state.storage.activeInstallation) ?? -1
  const { t } = useCustomTranslation()
  const { enqueueSuccessStack, enqueueErrorStack } = useSnackbar()
  const queryClient = useQueryClient()
  const queryKey = ['vendor-connections', installationId]

  const { mutate: createExtensionConnectionUrl, isError } = usePortalMutation<{ url: string }>({
    mutationFn: () => createVendorConnectionUrl(installationId.toString(), ExtensionName, authFetch),
    onSuccess: data => {
      setExtensionConnectionUrl(data?.url ?? null)
    },
    onError: error => {
      console.error('An error occurred while creating vendor connection:', error)
    },
  })

  const { mutate: handleConfirmationMutation } = usePortalMutation({
    mutationFn: ({ installationId, vendorAccountId }: { installationId?: string; vendorAccountId?: string }) => {
      return confirmVendorConnection(authFetch, installationId, vendorAccountId)
    },
    onSuccess: data => {
      if (data) {
        setConnectionState({ isConnectionConfirmed: true, isLoading: false, hasError: false, error: null })
        queryClient.invalidateQueries({ queryKey })
      } else {
        const error = new Error('Vendor connection could not be confirmed')
        console.error(error)
        setConnectionState({
          isConnectionConfirmed: false,
          isLoading: false,
          hasError: true,
          error,
        })
      }
    },
    onError: error => {
      console.error(error)
      setConnectionState({
        isConnectionConfirmed: false,
        isLoading: false,
        hasError: true,
        errorNumber: (error as AuthFetchError)?.response?.data?.errno,
        error,
      })
    },
  })

  const handleExtensionConnectionConfirmation = (installationId?: string, vendorAccountId?: string): void => {
    setConnectionState({ isConnectionConfirmed: false, isLoading: true, hasError: false, error: null })
    handleConfirmationMutation({ installationId, vendorAccountId })
  }

  const { mutateAsync: handleDeleteExtensionConnection } = usePortalMutation({
    mutationFn: () => {
      return deleteVendorConnection(installationId.toString(), ExtensionName, authFetch)
    },
    onSuccess: data => {
      if (data) {
        enqueueSuccessStack(t(`installation-extensions.${ExtensionName}.connection-deletion.success`))
        queryClient.invalidateQueries({ queryKey })
      } else {
        enqueueErrorStack(t(`installation-extensions.${ExtensionName}.connection-deletion.error`))
      }
    },
    onError: error => {
      console.error('An error occurred while deleting vendor connection:', error)
      enqueueErrorStack(t(`installation-extensions.${ExtensionName}.connection-deletion.error`))
    },
  })

  return {
    extensionConnectionUrl,
    createExtensionConnectionUrl,
    handleDeleteExtensionConnection,
    handleExtensionConnectionConfirmation,
    connectionState,
    hasConnectionUrlError: isError,
  }
}

export default useExtensionConnection
